import katex from 'katex';
// katex.render emits markup that is unreadable without this stylesheet; previously it only
// arrived by side effect of the rich-text-editor chunk, so preview-only pages rendered raw
// MathML at browser default sizes
import 'katex/dist/katex.css';
import { useMemo } from 'react';

import { HTMLView } from '../html-view';
import { useTheme } from '../theme';

export interface ContentPreviewerProps {
  content: string;
}

function ContentPreviewer(props: Readonly<ContentPreviewerProps>) {
  const { content } = props;
  const theme = useTheme();

  const parsedLatexContent = useMemo(() => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(content, 'text/html');

    const latexes = doc.querySelectorAll<HTMLSpanElement>('[data-latex]');

    for (const latexNode of Array.from(latexes)) {
      const latexAttr = latexNode.getAttribute('data-latex');

      if (latexAttr) {
        katex.render(latexAttr, latexNode, {
          throwOnError: false,
        });
      }
    }

    return doc.body.innerHTML;
  }, [content]);

  return (
    <HTMLView
      sx={{
        '& img': {
          display: 'block',
        },
        '& table': {
          borderCollapse: 'collapse',
          margin: 0,
          overflow: 'hidden',
          tableLayout: 'fixed',
          width: '100%',

          '& td, & th': {
            border: `1px solid ${theme.palette.grey[300]}`,
            boxSizing: 'border-box',
            minWidth: '1em',
            padding: theme.spacing(0.75, 1),
            position: 'relative',
            verticalAlign: 'top',

            '& > *': {
              marginBottom: 0,
            },
          },

          '& th': {
            backgroundColor: theme.palette.grey[100],
            fontWeight: theme.typography.fontWeightBold,
            textAlign: 'left',
          },

          '& .selectedCell:after': {
            background: theme.palette.grey[200],
            content: '""',
            left: 0,
            right: 0,
            top: 0,
            bottom: 0,
            pointerEvents: 'none',
            position: 'absolute',
            zIndex: 2,
          },

          '& .column-resize-handle': {
            backgroundColor: theme.palette.primary.main,
            bottom: -2,
            pointerEvents: 'none',
            position: 'absolute',
            right: -2,
            top: 0,
            width: 4,
          },
        },

        '& .tableWrapper': {
          margin: theme.spacing(1.5, 0),
          overflowX: 'auto',
        },
        '& p:first-of-type': {
          marginTop: 0,
        },
        '& p:last-of-type': {
          marginBottom: 0,
        },
      }}
    >
      {parsedLatexContent}
    </HTMLView>
  );
}

export { ContentPreviewer };
